import React from 'react';

/**
 * BulkActionBar — sticky top-of-view action bar shown when a bulk selection exists.
 *
 * Props:
 *   count       (required)  number of selected rows
 *   onClear     (required)  () => void — the "Clear" button handler
 *   actions     (required)  [{label, icon?, onClick, variant?, disabled?}]
 *                             variant: 'primary' | 'danger' | undefined (default = neutral white)
 *   busy?       boolean     disables all buttons while a mutation is in flight
 *   tone?       'primary'|'purple'|'emerald'|'amber' (default 'primary') — background tint
 *   summary?    ReactNode   optional extra text/chip in the bar (e.g. totals)
 *
 * Consolidates the ad-hoc bulk bars scattered across Matters.js / Tasks.js / TimeEntries.js
 * so every page can render an identical selection surface.  Legacy sites are NOT refactored
 * in this wave — that happens in Wave 4 G5.
 */

const TONE_STYLES = {
  primary: {
    wrapper: 'bg-primary-50 border-primary-200',
    chip: 'bg-primary-600 text-white',
    text: 'text-primary-900',
  },
  purple: {
    wrapper: 'bg-purple-50 border-purple-200',
    chip: 'bg-purple-600 text-white',
    text: 'text-purple-900',
  },
  emerald: {
    wrapper: 'bg-emerald-50 border-emerald-200',
    chip: 'bg-emerald-600 text-white',
    text: 'text-emerald-900',
  },
  amber: {
    wrapper: 'bg-amber-50 border-amber-200',
    chip: 'bg-amber-600 text-white',
    text: 'text-amber-900',
  },
};

const VARIANT_STYLES = {
  primary:
    'bg-primary-600 text-white border border-primary-600 hover:bg-primary-700 disabled:bg-primary-300 disabled:border-primary-300',
  danger:
    'bg-red-600 text-white border border-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:border-red-300',
  default:
    'bg-white text-gray-800 border border-gray-300 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400',
};

export default function BulkActionBar({
  count,
  onClear,
  actions,
  busy = false,
  tone = 'primary',
  summary = null,
}) {
  if (!count) return null;

  const t = TONE_STYLES[tone] || TONE_STYLES.primary;

  return (
    <div
      role="region"
      aria-label={`${count} selected`}
      className={`sticky top-2 z-10 shadow-lg rounded-lg px-4 py-2 flex items-center gap-3 border ${t.wrapper}`}
    >
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${t.chip}`}
      >
        {count} selected
      </span>

      {summary && (
        <span className={`text-sm ${t.text}`}>{summary}</span>
      )}

      <div className="ml-auto flex items-center gap-2 flex-wrap">
        {(actions || []).map((action, i) => {
          const variantClass =
            VARIANT_STYLES[action.variant] || VARIANT_STYLES.default;
          const disabled = busy || action.disabled;
          return (
            <button
              key={i}
              type="button"
              onClick={action.onClick}
              disabled={disabled}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium transition-colors disabled:cursor-not-allowed ${variantClass}`}
            >
              {action.icon && <span aria-hidden="true">{action.icon}</span>}
              <span>{action.label}</span>
            </button>
          );
        })}

        <button
          type="button"
          onClick={onClear}
          disabled={busy}
          className="inline-flex items-center px-3 py-1.5 rounded text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          Clear
        </button>
      </div>
    </div>
  );
}
